import AppTheme from "../theme/AppTheme";
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Button, Drawer, Tooltip } from 'antd';
import { CountBadge as Badge } from '../shared/CountBadge';
import { BellOutlined, ReloadOutlined } from "@ant-design/icons";
import axios from "axios";
import { API_DOMAIN } from "../constants";
import { useSocket } from "../contexts/SocketContext";
import { useAuth } from "../contexts/AuthContext";
import { RequestsTab } from "./RequestsTab";
import { useAccess } from "../../features/access/context/AccessProvider";
import { readStaffDirectory } from "../qv4/models/shared/staffDirectoryRead";

const playApprovalSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [[880, 0], [1100, 0.18]].forEach(([freq, delay]) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g);
      g.connect(ctx.destination);
      o.type = "sine";
      o.frequency.value = freq;
      g.gain.setValueAtTime(0.22, ctx.currentTime + delay);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + 0.35);
      o.start(ctx.currentTime + delay);
      o.stop(ctx.currentTime + delay + 0.35);
    });
  } catch {}
};

// Re-derived here (module scope) rather than importing another context's
// own copy — this only needs a GET, not a full reload-everything effect.
const fetchPendingCount = (head) =>
  axios.get(`${API_DOMAIN}/approval-requests?status=pending`, head)
    .then(r => {
      const list = r.data?.data ?? r.data ?? [];
      return Array.isArray(list) ? list.length : 0;
    });

export function ApprovalCenter() {
  const { authState } = useAuth();
  const { can } = useAccess();
  const { socket, isConnected } = useSocket();
  const head = useMemo(() => ({ headers: { accessToken: localStorage.getItem("accessToken") } }), []);

  const [pendingCount, setPendingCount] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [refreshSignal, setRefreshSignal] = useState(0);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [users, setUsers] = useState([]);

  const userId = authState?.id;
  const canSeeApprovals = can("approvals.view");

  // userMap for name lookups in RequestsTab
  const userMap = useMemo(() => {
    const m = {};
    users.forEach(u => { m[u.id] = u.name || u.username; });
    return m;
  }, [users]);

  // Fetch initial pending count + users
  useEffect(() => {
    if (!userId) return;
    fetchPendingCount(head).then(setPendingCount).catch(() => {});
    readStaffDirectory()
      .then(r => setUsers(r.data?.data ?? r.data ?? []))
      .catch(() => {});
  }, [userId]); // eslint-disable-line

  // Socket: live count updates + sound
  useEffect(() => {
    if (!socket) return;

    const onCreated = () => {
      setPendingCount(prev => prev + 1);
      playApprovalSound();
    };

    const onUpdated = (data) => {
      const newStatus = data?.data?.status;
      if (newStatus && newStatus !== "pending") {
        setPendingCount(prev => Math.max(0, prev - 1));
      }
    };

    socket.on("approval_request:created", onCreated);
    socket.on("approval_request:updated", onUpdated);
    return () => {
      socket.off("approval_request:created", onCreated);
      socket.off("approval_request:updated", onUpdated);
    };
  }, [socket]);

  // pendingCount only ever moves by +1/-1 off socket events above — if one
  // is missed (computer sleep, tab backgrounded, brief network drop) while
  // someone else actions a request, the badge drifts and stays wrong until
  // a full page reload. Same fix WatiChatContext uses for its own "reload
  // everything after an outage" signal, just watching this socket's own
  // connection instead of WATI's separate one, since that's the socket
  // approval_request:* actually arrives on. Re-fetches the authoritative
  // count the moment this socket comes back up from a real disconnect
  // (not the very first connect on page load, which isn't a recovery).
  const hasEverConnectedRef = useRef(false);
  const wasDownRef = useRef(false);
  useEffect(() => {
    if (!isConnected) {
      if (hasEverConnectedRef.current) wasDownRef.current = true;
      return;
    }
    hasEverConnectedRef.current = true;
    if (wasDownRef.current) {
      wasDownRef.current = false;
      if (userId) fetchPendingCount(head).then(setPendingCount).catch(() => {});
    }
  }, [isConnected]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRequestDecided = useCallback((id, newStatus) => {
    if (newStatus !== "pending") setPendingCount(prev => Math.max(0, prev - 1));
  }, []);

  if (!canSeeApprovals) return null;

  return (
    <AppTheme>
      <Tooltip title="Approval Requests" placement="bottom">
        <Badge count={pendingCount} size="small" offset={[-2, 2]}>
          <Button
            type="text"
            icon={<BellOutlined style={{ fontSize: 20, color: pendingCount > 0 ? "#faad14" : "white" }} />}
            onClick={() => setDrawerOpen(true)}
            style={{ padding: "4px 6px", display: "flex", alignItems: "center" }}
          />
        </Badge>
      </Tooltip>

      <Drawer
        title="Approval Requests"
        extra={<Button size="small" icon={<ReloadOutlined />} loading={requestsLoading} onClick={() => setRefreshSignal(value => value + 1)}>Refresh</Button>}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={520}
        styles={{ body: { padding: "0 16px 16px", display: "flex", flexDirection: "column" } }}
        destroyOnHidden={false}
      >
        <RequestsTab
          open={drawerOpen}
          refreshSignal={refreshSignal}
          onLoadingChange={setRequestsLoading}
          userMap={userMap}
          authState={authState}
          onRequestDecided={handleRequestDecided}
          onCountsLoaded={setPendingCount}
        />
      </Drawer>
    </AppTheme>
  );
}
